<template>
  <PageLoading v-if="isLoading"/>

  <div v-if="showExtensionsMessage">
    <div class="payjoy-top-banner">
      <img src="@/assets/icons/payjoy_logo.svg" alt="PayJoy"/>
    </div>
    <div class="extensions-message">
      <img v-if="extensionsMessageIcon" :src="extensionsMessageIcon" alt="Message icon"/>
      <div class="message">
        <h1>{{ extensionsMessageTitle }}</h1>
        <p>{{ extensionsMessageContent }}</p>
      </div>
      <div v-if="extensionsMessageButtonText" class="close-button-container">
        <button
          class="primary extension-continue-button"
          @click.stop="extensionsRetry"
        >
          {{ extensionsMessageButtonText }}
        </button>
      </div>
    </div>
  </div>

  <template v-if="!isLoading && !showExtensionsMessage">
    <div class="payjoy-top-banner">
      <img src="@/assets/icons/payjoy_logo.svg" alt="PayJoy"/>
    </div>
    <div class="providers-list">
      <h1 class="listing-titles">
        {{ t("why-do-you-need-extension") }}
      </h1>
      <div v-if="extensionReasons.length">
        <ul>
          <li
            v-for="reason in extensionReasons"
            :key="reason.reasonKey"
            :class="{ 'extension-option--selected': selectedExtensionReason === reason.reasonKey }"
          >
            <CardItem
              :title="t(reason.title)"
              :right-icon="selectedExtensionReason === reason.reasonKey ? checkboxSelectedIcon : checkboxEmptyIcon"
              right-icon-alt="Select option"
              @click="selectedExtensionReason = reason.reasonKey"
            />
          </li>
        </ul>
      </div>
    </div>
    <div class="close-button-container">
      <button class="primary extension-continue-button"
              :disabled="!selectedExtensionReason || disableButton"
              @click="selectExtensionReason">
        {{t("continue") }}
      </button>
    </div>
  </template>

  <HighlightPopup
    :is-visible="showPopup"
    :popup-class="popupClass"
    :add-close-icon="true"
    :button-text="modalButtonText"
    button-color="#222926"
    :image-src="extensionModalImage"
    @close-popup="onClosePopup"
    @dismiss-popup-close-btn="onDismissPopupCloseBtn"
  >
    <h1 class="text-black">
      {{ extensionModalTitle }}
    </h1>
    <MarkdownRenderer :content="extensionModalContent1" tag="p" class="extension-modal-content"/>
    <MarkdownRenderer :content="extensionModalContent2" tag="p" class="extension-modal-content"/>
  </HighlightPopup>

  <div v-if="showLoadingModal" class="loading-modal-overlay">
    <div class="loading-modal">
      <button class="popup-close-btn" @click="showLoadingModal = false">
        <img :src="closeSmallBlackIcon" alt="Close"/>
      </button>
      <img :src="loadingIconGreen" alt="Loading" class="loading-modal-icon"/>
      <p class="loading-modal-text">{{ t("loading") }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import {useRoute, useRouter} from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import CardItem from "@/components/CardItem.vue";
import { AnalyticEventsNames } from "@/helpers/google-analytics.helpers";
import authHelper from "@/helpers/authHelper";
import { IAuthError } from "@/types/auth";
import { ActionLogTypeConst } from "@/types/IActionLogs";
import { getFallbackLocale } from "@/i18n";
import {
  IExtensionReason,
  IInitiateExtensionResponse,
  ISelectExtensionRequest
} from "@/types/IExtensions";
import checkboxEmptyIcon from "@/assets/icons/checkbox_empty.svg";
import checkboxSelectedIcon from "@/assets/icons/checkbox_selected.svg";
import PageLoading from "@/components/PageLoading.vue";
import HighlightPopup from "@/components/HighlightPopup.vue";
import extensionClockIcon from "@/assets/icons/extension_clock.svg";
import extensionWarningIcon from "@/assets/icons/extension_warning.svg";
import extensionInfoIcon from "@/assets/icons/extension_info.svg";
import loadingIconGreen from "@/assets/icons/loading_icon_green.svg";
import closeSmallBlackIcon from "@/assets/icons/close_small_black.svg";
import MarkdownRenderer from "@/components/MarkdownRenderer.vue";
import {formatDateWeekday12h} from "@/shared-helpers/date-time.helper";
import {collapseTrailingPeriod} from "@/helpers/i18n.helper";

export default defineComponent({
  name: "ExtensionReasonView",
  components: {MarkdownRenderer, HighlightPopup, PageLoading, CardItem },
  emits: ["select-extension-reason"],
  setup() {
    const { t, te, n, locale } = useI18n();
    const router = useRouter();
    const store = useStore();

    const isLoading = ref(true);
    const showPopup = ref(false);
    const showLoadingModal = ref(false);

    const showExtensionsMessage = ref(false);
    const extensionsMessageTitle = ref("");
    const extensionsMessageContent = ref("");
    const extensionsMessageButtonText = ref("");
    const extensionsMessageIcon = ref("");

    const popupClass = ref("");
    const disableButton = ref(false);
    const extensionModalImage = ref(extensionClockIcon);
    const extensionModalTitle = ref(t("extensions-granted"));
    const extensionModalContent1 = ref("");
    const extensionModalContent2 = ref("");
    const modalButtonText = ref(t("extensions-confirm"));
    const deviceTag = computed(() => store.state.payOptions.deviceInfo.deviceTag);
    const uiReasonKeys = computed(() => store.state.payOptions.extensions.uiReasonKeys);
    const uiReasons = computed(() => store.state.payOptions.extensions.uiReasons?.split("#") ?? []);

    const extensionReasons = computed<IExtensionReason[]>(() =>
      uiReasonKeys.value
        .map((key: string, index: number) => ({ reasonKey: key, title: uiReasons.value[index] }))
        // The Extensions Service includes "Not connected to the internet" as a
      // reason key, but it is an offline-only fallback not applicable in a
      // web context, so it is always excluded from the selectable list.
      .filter(({ reasonKey }) => reasonKey.toLowerCase() !== "not connected to the internet")
    );
    const extensionRequestData = ref<IInitiateExtensionResponse | null>(null);
    const selectedExtensionReason = ref<string | null>(null);
    const routerGuardUnsubscribe = ref<(() => void) | null>(null);
    isLoading.value = false;

    if(!deviceTag.value || !store.state.payOptions.extensions.uiReasons) {
      showExtensionsMessage.value = true;
      extensionsMessageIcon.value = extensionInfoIcon;
      extensionsMessageTitle.value = t("extensions-not-available");
    }

    return {
      t,
      locale,
      router,
      deviceTag,
      store,
      isLoading,
      showPopup,
      showLoadingModal,
      loadingIconGreen,
      closeSmallBlackIcon,
      popupClass,
      disableButton,
      extensionReasons,
      extensionRequestData,
      checkboxEmptyIcon,
      checkboxSelectedIcon,
      selectedExtensionReason,
      extensionModalImage,
      extensionModalTitle,
      extensionModalContent1,
      extensionModalContent2,
      modalButtonText,
      showExtensionsMessage,
      extensionsMessageTitle,
      extensionsMessageContent,
      extensionsMessageButtonText,
      extensionsMessageIcon,
      routerGuardUnsubscribe,
    };
  },
  data() {
    return {

    };
  },
  watch: {

  },
  mounted() {
    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page view
      authHelper.logAction(
        {
          deviceTag: this.deviceTag,
          action: AnalyticEventsNames.OnSelectExtensionReasonPage,
          actionType: ActionLogTypeConst.PAGE_VIEW,
        },
        this.$gtag,
      );
    }
    this.routerGuardUnsubscribe = this.router.beforeEach((to, _from, next) => {
      if (to.name === "ExtensionTypeView") {
        if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
          authHelper.logAction(
            {
              deviceTag: this.deviceTag,
              action: AnalyticEventsNames.OnSelectExtensionReasonPage_BrowserGoBack,
              actionType: ActionLogTypeConst.BUTTON_CLICK,
            },
            this.$gtag,
          );
        }
      }
      next();
    });
  },
  beforeUnmount() {
    this.routerGuardUnsubscribe?.();
  },
  methods: {
    async selectExtensionReason() {
      try {
        this.disableButton = true;
        this.popupClass = "";
        this.showLoadingModal = true;
        this.$emit("select-extension-reason", this.selectedExtensionReason);

        const deviceTag = this.store.state.payOptions.deviceInfo.deviceTag;
        const requestId = this.store.state.payOptions.extensions.requestId;
        const idemKey = this.store.state.payOptions.extensions.idempotencyKey;
        const extensionType = this.store.state.payOptions.extensions.extensionType;
        const extensionTime = this.store.state.payOptions.extensions.extensionTime;
        const reason = this.selectedExtensionReason;

        const requiredValues = { deviceTag, requestId, idemKey, extensionType, extensionTime, reason };
        const missingValues = Object.entries(requiredValues)
          .filter(([, value]) => !value)
          .map(([key]) => key);

        if (missingValues.length) {
          console.error("Error in select extension: missing required values:", missingValues.join(", "));
          this.disableButton = false;
          this.showLoadingModal = false;
          return;
        }

        const params: ISelectExtensionRequest = {
          idempotencyKey: idemKey,
          extension_reason: reason!,
          extension_type: extensionType,
          extension_time: extensionTime
        };

        const response = await authHelper.grantExtension(requestId, params);
        if (response) {
          if(response.success && response.status === "GRANTED") {
            this.popupClass = "extension-granted-popup";
            const newDate = formatDateWeekday12h(response.newValidThrough, this.locale);
            this.extensionModalImage = extensionClockIcon;
            this.extensionModalTitle = this.t("extensions-granted");
            this.extensionModalContent1 = collapseTrailingPeriod(this.t("extensions-new-payment-date", {date: newDate}));
            this.extensionModalContent2 = "";
            this.modalButtonText = this.t("extensions-confirm");
          }
          else {
            this.extensionModalImage = extensionWarningIcon;
            this.extensionModalTitle = this.t("extensions-request-error");
            this.extensionModalContent1 = this.t("extensions-request-failure");
            this.extensionModalContent2 = this.t("extensions-please-try-again");
            this.modalButtonText = this.t("extensions-try-again");
          }
        }
      } catch (error) {
        console.error("Error in select extension request:", error);

        this.extensionModalImage = extensionWarningIcon;
        this.extensionModalTitle = this.t("extensions-request-error");
        this.extensionModalContent1 = this.t("extensions-request-failure");
        this.extensionModalContent2 = this.t("extensions-please-try-again");
        this.modalButtonText = this.t("extensions-try-again");
      }
      this.disableButton = false;
      this.showLoadingModal = false;
      this.showPopup = true;
    },
    onDismissPopupCloseBtn() {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            action: AnalyticEventsNames.OnSelectExtensionReasonPage_PopupDismiss,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
            data: {
              deviceTag: this.deviceTag,
              extensionReason: this.selectedExtensionReason
            },
          },
          this.$gtag,
        );
      }
      this.router.replace("/extensions/device/" + this.deviceTag);
    },
    onClosePopup() {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            action: AnalyticEventsNames.OnSelectExtensionReasonPage_PopupConfirm,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
            data: {
              deviceTag: this.deviceTag,
              extensionReason: this.selectedExtensionReason
            },
          },
          this.$gtag,
        );
      }
      this.router.replace("/extensions/device/" + this.deviceTag);
    },
  },
});
</script>

<style scoped>
.providers-list {
  padding: 16px 16px 128px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.providers-list :deep(.card-item .bold) {
  font-weight: inherit;
}

.providers-list h1 {
  color: #333;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.26px;
  margin: 0;
}

h3 {
  margin: 16px 0 0 0;
}

.providers-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.providers-list ul > li {
  position: relative;
}

.extension-option--selected :deep(.card-item) {
  border: 2px solid #222926;
}

.message h1 {
  color: var(--Primary-Green-1);
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px; /* 120% */
}

.message h2 {
  color: var(--Typography-Text-2);
  font-feature-settings:
    "clig" off,
    "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
}

.extension-continue-button {
  color: #fff;
  background-color: #222926;
}
.extension-continue-button:hover {
  background-color: #000 !important;
}
.extension-continue-button:disabled {
  background-color: #C6C7BF !important;
}
.payjoy-top-banner {
  border-bottom: 2px solid #D4D4D4;
  padding: 15px;
}

@media (min-width: 768px) {
  .payjoy-top-banner {
    background-color: #fff;
    display: flex;
    justify-content: center;
  }
  .providers-list {
    max-width: 840px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
  .close-button-container {
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }
  .listing-titles {
    padding-bottom: 1em;
  }
}
.extension-modal-content {
  color:#3F4340;
}
.close-button-container {
  padding: 0 20px 30px;
}
:deep(.extension-granted-popup .popup-image) {
  margin: 45px auto -30px 0 !important;
}
:deep(.extension-granted-popup .message),
:deep(.extension-granted-popup .message *) {
  text-align: left !important;
}
.extensions-message {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  min-height: 200px;
  top: 50%;
  left: 10px;
  right: 10px;
  transform: translateY(-50%);
  max-width: 480px;
  margin: 0 auto;
  padding: 20px;
  border-radius: 30px;
  text-align: center;
}
.extensions-message p {
  margin: 1.5em 0;
}
.extensions-message :deep(.message) * {
  color:#3F4340;
}
.extensions-message img {
  width: 60px;
  height: 60px;
  margin: 0 auto;
}
.extensions-message .close-button-container {
  padding: 0;
  max-width: none;
  margin: 0;
}
.extensions-message .extension-continue-button {
  width: 100%;
}

/* On mobile, dock the continue button to the bottom of the viewport.
   Placed last so it wins the cascade over the base .close-button-container. */
@media (max-width: 767px) {
  .close-button-container {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 16px 20px;
  }
  .close-button-container .extension-continue-button {
    width: 100%;
  }
  /* Keep the retry button inside the centered error card, not docked */
  .extensions-message .close-button-container {
    position: static;
    padding: 0;
  }
}

/* On desktop the popup is a centered modal, so center its text and icon too
   (overrides the left-aligned granted-popup layout used on mobile). */
@media (min-width: 768px) {
  /* Present the popup as a centered modal instead of a full-width bottom
     sheet. Scoped to the extension views (previously lived in HighlightPopup,
     which leaked the styling to every other popup usage).
     The overlay's own `align-items: flex-end` can't be overridden from here
     because `:deep(.overlay)` targets the child's root element, which scoped
     `:deep()` cannot reach; `align-self: center` on the popup flex item
     overrides that alignment for just this item instead. */
  :deep(.popup) {
    align-self: center;
    max-width: min(84%, 1000px);
    border-radius: 16px;
    animation: fadeIn 0.2s ease-out;
  }
  :deep(.close-button-container) {
    padding-bottom: 12px;
  }
  :deep(.extension-granted-popup .popup-image) {
    margin: 45px auto -30px auto !important;
  }
  :deep(.extension-granted-popup .message),
  :deep(.extension-granted-popup .message *) {
    text-align: center !important;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Loading modal shown while the extension grant request is in flight.
   Follows the HighlightPopup pattern: full-width bottom sheet on mobile,
   centered modal on desktop. */
.loading-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
}
.loading-modal {
  position: relative;
  width: 100%;
  min-height: 250px;
  background: #fff;
  border-radius: 10px 10px 0 0;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  animation: loading-modal-slide-up 0.3s ease-out;
}
.loading-modal .popup-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: auto;
  background: none;
  border: none;
  padding: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}
.loading-modal .popup-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.08);
}
.loading-modal-icon {
  width: 48px;
  height: 48px;
  animation: loading-modal-spin 1s linear infinite;
}
.loading-modal-text {
  margin: 0;
  color: #3F4340;
  font-size: 16px;
  font-weight: 500;
}
@keyframes loading-modal-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loading-modal-slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@media (min-width: 768px) {
  .loading-modal-overlay {
    align-items: center;
  }
  .loading-modal {
    max-width: 400px;
    border-radius: 16px;
    animation: loading-modal-fade-in 0.2s ease-out;
  }
}
@keyframes loading-modal-fade-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
